import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  BarChart3,
  Sparkles,
  FileSearch,
  ArrowRight,
  X,
  Database,
  Trash2,
  Search,
  ChevronDown,
  AlertTriangle,
  FolderPlus,
  ListTree,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { DashboardSkeleton } from '../../components/LoadingSkeleton';
import { useToast } from '../../contexts/ToastContext';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '../../utils/deleteConfirmation';
import type { PdfAnalysisListItem, ExtractionMode } from '../../types/pdf-analysis.types';
import { useAuthStore } from '../../store/authStore';
import Swal from 'sweetalert2';
import { portfolioService } from '../../services/portfolio.service';

export function PdfAnalysisList() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Check permissions
  const canDelete = user?.permissions?.includes('uploaded_booklets_delete') || false;

  const [showUploadModal, setShowUploadModal] = useState(false);
  // NQG-RQ-29 (client feedback): choose portfolios at upload time
  const [uploadPortfolios, setUploadPortfolios] = useState<string[]>([]);
  const { data: portfoliosList = [] } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
    retry: false,
  });
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Fetch analyses list
  const { data: analyses = [], isLoading } = useQuery({
    queryKey: ['pdf-analyses'],
    queryFn: () => pdfAnalysisService.getAnalyses(),
    refetchInterval: (query) => {
      const data = query.state.data as PdfAnalysisListItem[] | undefined;
      const hasProcessing = data?.some(
        (a) => a.status === 'pending' || a.status === 'processing'
      );
      return hasProcessing ? 5000 : false;
    },
  });

  // Fetch question bank count (PDF-generated questions)
  const { data: questionsData } = useQuery({
    queryKey: ['pdf-questions-count'],
    queryFn: () => pdfAnalysisService.getGeneratedQuestions({ limit: 1 }),
  });
  const questionBankCount = questionsData?.pagination?.total || 0;

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File; mode: ExtractionMode }) => {
      return pdfAnalysisService.analyzePdf(file, 'anbima');
    },
    onSuccess: async (data) => {
      // Assign chosen portfolios to the newly created analysis (client feedback)
      if (uploadPortfolios.length > 0) {
        try {
          await portfolioService.setPdfPortfolios(data._id, uploadPortfolios);
        } catch {
          /* non-blocking — the PDF is uploaded regardless */
        }
      }
      queryClient.invalidateQueries({ queryKey: ['pdf-analyses'] });
      showToast('success', 'PDF Queued', 'Your PDF analysis has been queued for background processing.');
      setShowUploadModal(false);
      setUploadingFile(null);
      setUploadPortfolios([]);
      // Navigate to detail so user can watch progress
      navigate(`/pdf-analysis/${data._id}`);
    },
    onError: (error: any) => {
      showToast('error', 'Upload Failed', error.response?.data?.error || error.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return pdfAnalysisService.deleteAnalysis(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pdf-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['pdf-questions-count'] });
      showDeleteSuccess('PDF analysis has been deleted successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || error.message);
    },
  });

  const handleDelete = async (id: string, filename: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const confirmed = await confirmDelete({
      title: 'Delete PDF Analysis?',
      itemName: filename,
    });

    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  // NQG-RQ-29: assign this PDF to one or more portfolios (multi-select)
  const handleAssignPortfolios = async (analysis: PdfAnalysisListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    let portfolios;
    try {
      portfolios = await portfolioService.list();
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: 'Could not load portfolios.', icon: 'error', confirmButtonColor: '#dc2626' });
      return;
    }
    const current = new Set(analysis.portfolios || []);
    const checkboxes = portfolios
      .map(
        (p) => `<label style="display:flex;align-items:center;gap:8px;padding:4px 0;text-align:left">
          <input type="checkbox" value="${p.name}" ${current.has(p.name) ? 'checked' : ''}> ${p.name}${p.isDefault ? ' (default)' : ''}
        </label>`
      )
      .join('');

    const { value, isConfirmed } = await Swal.fire({
      title: `Portfolios for "${analysis.filename}"`,
      html: `<div id="pfCheckboxes" style="max-height:260px;overflow-y:auto">${checkboxes || '<p>No portfolios yet — create one first.</p>'}</div>`,
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#1e73be',
      focusConfirm: false,
      preConfirm: () => {
        const container = document.getElementById('pfCheckboxes');
        const checked = Array.from(
          container?.querySelectorAll<HTMLInputElement>('input[type=checkbox]') || []
        )
          .filter((el) => el.checked)
          .map((el) => el.value);
        // Return an object so an empty array (deselect all) still passes through
        return { portfolios: checked };
      },
    });
    if (!isConfirmed || !value) return;
    try {
      await portfolioService.setPdfPortfolios(analysis._id, value.portfolios || []);
      queryClient.invalidateQueries({ queryKey: ['pdf-analyses'] });
      Swal.fire({
        title: 'Saved',
        text: `Portfolios updated for "${analysis.filename}".`,
        icon: 'success',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.response?.data?.error || 'Could not save portfolios.', icon: 'error', confirmButtonColor: '#dc2626' });
    }
  };

  // NQG-RQ-31: place this PDF on taxonomy nodes (any level, any portfolio's
  // tree). Checkbox list of full paths, grouped by portfolio; the flat
  // portfolio tag is added automatically server-side.
  const handleAssignTaxonomy = async (analysis: PdfAnalysisListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    let portfolios;
    try {
      portfolios = await portfolioService.list();
    } catch {
      Swal.fire({ title: 'Error', text: 'Could not load portfolios.', icon: 'error', confirmButtonColor: '#dc2626' });
      return;
    }

    const groups: string[] = [];
    for (const p of portfolios) {
      try {
        const nodes = await portfolioService.getTaxonomy(p._id);
        if (nodes.length === 0) continue;
        const byId = new Map(nodes.map((n) => [String(n._id), n]));
        const pathOf = (id: string): string => {
          const names: string[] = [];
          let cur = byId.get(id);
          while (cur) {
            names.unshift(cur.name);
            cur = cur.parentId ? byId.get(String(cur.parentId)) : undefined;
          }
          return names.join(' › ');
        };
        const current = new Set((analysis.taxonomyPlacements || []).map((tp) => tp.nodeId));
        const rows = nodes
          .map((n) => ({ n, path: pathOf(String(n._id)) }))
          .sort((a, b) => a.path.localeCompare(b.path))
          .map(
            ({ n, path }) => `<label style="display:flex;align-items:center;gap:8px;padding:3px 0;text-align:left;margin-left:${(n.level - 1) * 16}px">
              <input type="checkbox" value="${n._id}" ${current.has(String(n._id)) ? 'checked' : ''}> <span style="font-size:13px">${path}</span>
            </label>`
          )
          .join('');
        groups.push(`<p style="text-align:left;font-weight:700;font-size:12px;color:#7c3aed;margin:10px 0 4px;text-transform:uppercase">${p.name}</p>${rows}`);
      } catch {
        // portfolio without accessible taxonomy — skip
      }
    }

    if (groups.length === 0) {
      Swal.fire({
        title: 'No taxonomy defined yet',
        text: 'Create Themes in Portfolios → Taxonomy first, then place this PDF.',
        icon: 'info',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const { value, isConfirmed } = await Swal.fire({
      title: `Taxonomy for "${analysis.filename}"`,
      html: `<div id="taxCheckboxes" style="max-height:300px;overflow-y:auto">${groups.join('')}</div>
             <p style="font-size:11px;color:#888;margin-top:8px;text-align:left">A PDF can sit at any level — Theme only, Theme › Subtheme, or the full path.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#1e73be',
      focusConfirm: false,
      width: 620,
      preConfirm: () => {
        const container = document.getElementById('taxCheckboxes');
        const checked = Array.from(
          container?.querySelectorAll<HTMLInputElement>('input[type=checkbox]') || []
        )
          .filter((el) => el.checked)
          .map((el) => el.value);
        return { nodeIds: checked };
      },
    });
    if (!isConfirmed || !value) return;
    try {
      await portfolioService.setPdfTaxonomy(analysis._id, value.nodeIds || []);
      queryClient.invalidateQueries({ queryKey: ['pdf-analyses'] });
      Swal.fire({
        title: 'Saved',
        text: `Taxonomy placement updated for "${analysis.filename}".`,
        icon: 'success',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.response?.data?.error || 'Could not save taxonomy.', icon: 'error', confirmButtonColor: '#dc2626' });
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadingFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
  });

  const handleUpload = async () => {
    if (!uploadingFile) return;

    // Check if PDF with same filename already exists
    const existingAnalysis = analyses.find(
      (analysis) => analysis.filename.toLowerCase() === uploadingFile.name.toLowerCase()
    );

    if (existingAnalysis) {
      const Swal = (await import('sweetalert2')).default;
      const result = await Swal.fire({
        title: 'PDF Already Uploaded',
        html: `<p class="text-gray-600 mb-2">A PDF with the name <strong>${uploadingFile.name}</strong> has already been uploaded.</p><p class="text-sm text-blue-600">Would you like to view the existing analysis?</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1e73be',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Use Existing',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'rounded-2xl',
        },
      });

      if (result.isConfirmed) {
        // Navigate to existing analysis
        setShowUploadModal(false);
        setUploadingFile(null);
        navigate(`/pdf-analysis/${existingAnalysis._id}`);
      }
      return;
    }

    uploadMutation.mutate({ file: uploadingFile, mode: 'auto' });
  };

  // Stats — old docs without status field are effectively completed
  const totalAnalyses = analyses.length;
  const getStatus = (a: PdfAnalysisListItem) => a.status || 'completed';
  const completedCount = analyses.filter((a) => getStatus(a) === 'completed').length;
  const processingCount = analyses.filter(
    (a) => getStatus(a) === 'pending' || getStatus(a) === 'processing'
  ).length;
  const totalItems = analyses.reduce((sum, a) => sum + (a.totalItems || 0), 0);

  // Filter analyses based on search term
  const filteredAnalyses = analyses.filter((analysis) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      analysis.filename.toLowerCase().includes(search) ||
      (analysis.extractionMode && analysis.extractionMode.toLowerCase().includes(search))
    );
  });

  // Pagination calculations
  const totalFiltered = filteredAnalyses.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAnalyses = filteredAnalyses.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (isLoading) return <DashboardSkeleton />;

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="PDF Analysis"
          description="Upload PDFs for AI-powered content analysis and structured extraction"
          breadcrumbs={[{ label: 'PDF Analysis' }]}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pdf-analysis/generate')}
            className="btn-secondary flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            <Sparkles className="w-4 h-4" />
            Generate Questions
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            <Upload className="w-4 h-4" />
            Analyze PDF
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard
          icon={FileText}
          label="Total Analyses"
          value={totalAnalyses}
          color="blue"
        />
        <StatsCard
          icon={CheckCircle2}
          label="Completed"
          value={completedCount}
          color="green"
        />
        <StatsCard
          icon={Loader2}
          label="Processing"
          value={processingCount}
          color="amber"
          pulse={processingCount > 0}
        />
        <StatsCard
          icon={Sparkles}
          label="Topics Extracted"
          value={totalItems}
          color="purple"
        />
        <StatsCard
          icon={Database}
          label="Question Bank"
          value={questionBankCount}
          color="indigo"
          onClick={() => navigate('/pdf-analysis/question-bank')}
        />
      </div>

      {/* Search Filter */}
      {analyses.length > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl border-2 border-gray-100 shadow-lg hover:shadow-xl transition-shadow p-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search PDFs by filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all text-sm font-medium placeholder:text-gray-400 bg-white shadow-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear search"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {filteredAnalyses.length === 0 && searchTerm ? (
        <div className="card">
          <EmptyState
            icon={Search}
            title="No results found"
            description={`No PDFs match "${searchTerm}". Try a different search term.`}
            action={{
              label: 'Clear Search',
              onClick: () => setSearchTerm(''),
            }}
          />
        </div>
      ) : analyses.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={FileSearch}
            title="No analyses yet"
            description="Upload a PDF to get started with AI-powered content extraction and analysis."
            action={{
              label: 'Upload PDF',
              onClick: () => setShowUploadModal(true),
            }}
          />
        </div>
      ) : (
        <>
          {/* Pagination Info & Controls */}
          {totalFiltered > 0 && (
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalFiltered)} of {totalFiltered}
                  </span>
                  <span className="text-sm text-gray-600">analyses</span>

                  {/* Items Per Page Selector */}
                  <div className="flex items-center gap-2 ml-4">
                    <span className="text-xs font-medium text-gray-600">Show:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 text-sm font-bold border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all hover:border-gray-300 bg-white shadow-sm"
                    >
                      <option value={6}>6</option>
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                    </select>
                    <span className="text-xs font-medium text-gray-600">per page</span>
                  </div>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    {/* First Page */}
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="p-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-110 bg-white shadow-sm"
                      title="First Page"
                    >
                      <ChevronDown className="w-4 h-4 rotate-90" />
                    </button>

                    {/* Previous Page */}
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 bg-white shadow-sm"
                    >
                      <ChevronDown className="w-4 h-4 rotate-90" />
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="hidden md:flex items-center gap-2">
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show first page, last page, current page, and pages around current
                        const showPage =
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                        // Show ellipsis
                        const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                        const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;

                        if (showEllipsisBefore || showEllipsisAfter) {
                          return (
                            <span key={i} className="px-2 text-gray-400">
                              ...
                            </span>
                          );
                        }

                        if (!showPage) return null;

                        return (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`min-w-[40px] h-10 rounded-xl border-2 text-sm font-bold transition-all hover:scale-110 shadow-sm ${
                              currentPage === pageNum
                                ? 'bg-gradient-to-r from-[#1e73be] to-[#4A90C8] border-[#1e73be] text-white shadow-lg'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    {/* Mobile: Current Page Indicator */}
                    <div className="md:hidden flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] rounded-xl border-2 border-[#1e73be] shadow-lg">
                      <span className="text-sm font-bold text-white">
                        {currentPage} / {totalPages}
                      </span>
                    </div>

                    {/* Next Page */}
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 bg-white shadow-sm"
                    >
                      Next
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </button>

                    {/* Last Page */}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-110 bg-white shadow-sm"
                      title="Last Page"
                    >
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PDF Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginatedAnalyses.map((analysis) => (
              <AnalysisCard
                key={analysis._id}
                analysis={analysis}
                onClick={() => navigate(`/pdf-analysis/${analysis._id}`)}
                onDelete={(e) => handleDelete(analysis._id, analysis.filename, e)}
                onAssignPortfolios={(e) => handleAssignPortfolios(analysis, e)}
                onAssignTaxonomy={(e) => handleAssignTaxonomy(analysis, e)}
                canDelete={canDelete}
              />
            ))}
          </div>
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          isDragActive={isDragActive}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          uploadingFile={uploadingFile}
          setUploadingFile={setUploadingFile}
          isUploading={uploadMutation.isPending}
          onUpload={handleUpload}
          portfolios={portfoliosList}
          selectedPortfolios={uploadPortfolios}
          setSelectedPortfolios={setUploadPortfolios}
          onClose={() => {
            setShowUploadModal(false);
            setUploadingFile(null);
            setUploadPortfolios([]);
          }}
        />
      )}
    </div>
  );
}

// ── Stats Card ──────────────────────────────────────────────────────────────

function StatsCard({
  icon: Icon,
  label,
  value,
  color,
  pulse = false,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
  pulse?: boolean;
  onClick?: () => void;
}) {
  const colorMap: Record<string, { bg: string; icon: string; text: string; border: string }> = {
    blue: {
      bg: 'from-blue-50 to-blue-100/50',
      icon: 'bg-blue-100 text-blue-600',
      text: 'text-blue-700',
      border: 'border-blue-300',
    },
    green: {
      bg: 'from-emerald-50 to-green-100/50',
      icon: 'bg-green-100 text-green-600',
      text: 'text-green-700',
      border: 'border-green-300',
    },
    amber: {
      bg: 'from-amber-50 to-orange-100/50',
      icon: 'bg-amber-100 text-amber-600',
      text: 'text-amber-700',
      border: 'border-amber-300',
    },
    purple: {
      bg: 'from-purple-50 to-violet-100/50',
      icon: 'bg-purple-100 text-purple-600',
      text: 'text-purple-700',
      border: 'border-purple-300',
    },
    indigo: {
      bg: 'from-indigo-50 to-indigo-100/50',
      icon: 'bg-indigo-100 text-indigo-600',
      text: 'text-indigo-700',
      border: 'border-indigo-300',
    },
  };

  const c = colorMap[color] || colorMap.blue;

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick}
      className={`bg-gradient-to-br ${c.bg} rounded-2xl border-2 ${c.border} p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 ${onClick ? 'cursor-pointer' : ''} w-full text-left`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-600 uppercase tracking-wide">{label}</p>
          <p className={`text-3xl font-black mt-1 ${c.text}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${c.icon} shadow-md ${pulse ? 'animate-pulse' : ''}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Tag>
  );
}

// ── Analysis Card ───────────────────────────────────────────────────────────

function AnalysisCard({
  analysis,
  onClick,
  onDelete,
  onAssignPortfolios,
  onAssignTaxonomy,
  canDelete = true,
}: {
  analysis: PdfAnalysisListItem;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
  onAssignPortfolios: (e: React.MouseEvent) => void;
  onAssignTaxonomy: (e: React.MouseEvent) => void;
  canDelete?: boolean;
}) {
  const status = analysis.status || 'completed';
  const isProcessing = status === 'pending' || status === 'processing';
  const difficulty = analysis.difficultyDistribution;
  const total = (difficulty?.easy || 0) + (difficulty?.medium || 0) + (difficulty?.hard || 0);
  // NQG-RQ-26 v2: analysis completed but no topics could be identified
  const noTopicsIdentified = status === 'completed' && analysis.totalItems === 0;

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl border-2 border-gray-100 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 w-full relative overflow-hidden cursor-pointer"
    >
      {/* Processing shimmer overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/60 to-transparent animate-pulse pointer-events-none" />
      )}

      <div className="relative">
        {/* Top row: icon + status + delete */}
        <div className="flex items-start justify-between mb-3">
          <div className="p-2.5 bg-gradient-to-br from-[#1e73be]/10 to-[#ea7500]/10 rounded-xl">
            <FileText className="w-5 h-5 text-[#1e73be]" />
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={status} />
            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(e);
                }}
                className="relative p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 opacity-0 group-hover:opacity-100"
                aria-label="Delete analysis"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filename */}
        <h3 className="font-semibold text-gray-900 truncate mb-1 group-hover:text-[#1e73be] transition-colors">
          {analysis.filename}
        </h3>

        {/* NQG-RQ-29: portfolios + assign */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {(analysis.portfolios || []).length === 0 ? (
            <span className="text-[10px] text-gray-400 italic">FK (default)</span>
          ) : (
            (analysis.portfolios || []).map((pf) => (
              <span key={pf} className="inline-flex px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700">
                {pf}
              </span>
            ))
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onAssignPortfolios(e); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-gray-300 text-[10px] font-semibold text-gray-500 hover:border-[#1e73be] hover:text-[#1e73be] transition-colors"
            title="Assign portfolios"
          >
            <FolderPlus className="w-3 h-3" /> Portfolios
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onAssignTaxonomy(e); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-gray-300 text-[10px] font-semibold text-gray-500 hover:border-purple-500 hover:text-purple-600 transition-colors"
            title="Place on taxonomy (Theme → Subtheme → Sub-subtheme)"
          >
            <ListTree className="w-3 h-3" /> Taxonomy
          </button>
        </div>

        {/* NQG-RQ-31: taxonomy path chips */}
        {(analysis.taxonomyPlacements || []).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {(analysis.taxonomyPlacements || []).map((tp) => (
              <span
                key={tp.nodeId}
                className="inline-flex px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-700"
                title="Taxonomy path"
              >
                {(tp.pathNames || []).join(' › ')}
              </span>
            ))}
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <p className="text-xs text-gray-500">
            {analysis.totalPages} pages
            {analysis.totalItems > 0 && ` \u00B7 ${analysis.totalItems} topics`}
            {analysis.extractionMode && ` \u00B7 ${analysis.extractionMode}`}
          </p>
          {noTopicsIdentified && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-bold border border-amber-200"
              title="No topics could be identified in this document"
            >
              <AlertTriangle className="w-3 h-3" />
              No topics
            </span>
          )}
        </div>

        {/* Difficulty bar */}
        {total > 0 && (
          <div className="mb-3">
            <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-gray-100">
              {difficulty!.easy > 0 && (
                <div
                  className="bg-emerald-400 rounded-full"
                  style={{ width: `${(difficulty!.easy / total) * 100}%` }}
                />
              )}
              {difficulty!.medium > 0 && (
                <div
                  className="bg-amber-400 rounded-full"
                  style={{ width: `${(difficulty!.medium / total) * 100}%` }}
                />
              )}
              {difficulty!.hard > 0 && (
                <div
                  className="bg-red-400 rounded-full"
                  style={{ width: `${(difficulty!.hard / total) * 100}%` }}
                />
              )}
            </div>
            <div className="flex justify-between mt-1.5 text-[10px] text-gray-400 font-medium">
              <span>{difficulty!.easy} Easy</span>
              <span>{difficulty!.medium} Medium</span>
              <span>{difficulty!.hard} Hard</span>
            </div>
          </div>
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {status === 'pending' ? 'Queued...' : 'Analyzing with AI...'}
          </div>
        )}

        {status === 'failed' && (
          <div className="flex items-center gap-2 text-xs text-red-500 font-medium">
            <XCircle className="w-3.5 h-3.5" />
            Analysis failed
          </div>
        )}

        {/* Date + arrow */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            {new Date(analysis.analyzedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#1e73be] group-hover:translate-x-1 transition-all duration-300" />
        </div>
      </div>
    </div>
  );
}

// ── Upload Modal ────────────────────────────────────────────────────────────

function UploadModal({
  isDragActive,
  getRootProps,
  getInputProps,
  uploadingFile,
  setUploadingFile,
  isUploading,
  onUpload,
  portfolios,
  selectedPortfolios,
  setSelectedPortfolios,
  onClose,
}: {
  isDragActive: boolean;
  getRootProps: any;
  getInputProps: any;
  uploadingFile: File | null;
  setUploadingFile: (f: File | null) => void;
  isUploading: boolean;
  onUpload: () => void;
  portfolios: { _id: string; name: string; isDefault: boolean }[];
  selectedPortfolios: string[];
  setSelectedPortfolios: (p: string[]) => void;
  onClose: () => void;
}) {
  const toggle = (name: string) => {
    setSelectedPortfolios(
      selectedPortfolios.includes(name)
        ? selectedPortfolios.filter((n) => n !== name)
        : [...selectedPortfolios, name]
    );
  };
  const addNew = async () => {
    const Swal = (await import('sweetalert2')).default;
    const { value } = await Swal.fire({
      title: 'New portfolio',
      input: 'text',
      inputPlaceholder: 'Portfolio name',
      showCancelButton: true,
      confirmButtonText: 'Add',
      confirmButtonColor: '#1e73be',
      inputValidator: (v) => (!v.trim() ? 'Name is required' : undefined),
    });
    if (value?.trim() && !selectedPortfolios.includes(value.trim())) {
      // Selected now; the backend auto-creates it on assignment
      setSelectedPortfolios([...selectedPortfolios, value.trim()]);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Enhanced Backdrop */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Enhanced Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden animate-in">
        {/* Enhanced Header */}
        <div className="relative bg-gradient-to-r from-[#1e73be] via-[#4A90C8] to-[#5BA5D8] px-8 py-6">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl -translate-x-16 -translate-y-16" />
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full blur-3xl translate-x-24 translate-y-24" />
          </div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 backdrop-blur-sm rounded-2xl shadow-lg">
                <Sparkles className="w-7 h-7 text-white drop-shadow-md" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white drop-shadow-md">Analyze PDF</h2>
                <p className="text-sm text-white/90 font-medium mt-0.5">AI-powered content extraction</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-6 bg-gradient-to-br from-gray-50 to-white">
          {/* Enhanced Drop zone */}
          <div
            {...getRootProps()}
            className={`relative border-3 border-dashed rounded-3xl p-10 text-center cursor-pointer transition-all duration-300 overflow-hidden ${
              isDragActive
                ? 'border-[#1e73be] bg-gradient-to-br from-blue-50 to-blue-100/50 scale-[1.02] shadow-xl'
                : uploadingFile
                ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
                : 'border-gray-300 hover:border-[#1e73be] hover:bg-gradient-to-br hover:from-blue-50/50 hover:to-purple-50/30 hover:shadow-lg'
            }`}
          >
            <input {...getInputProps()} />
            {uploadingFile ? (
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl shadow-lg">
                  <FileText className="w-10 h-10 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{uploadingFile.name}</p>
                  <p className="text-sm text-gray-600 mt-1 font-semibold">
                    {(uploadingFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadingFile(null);
                  }}
                  className="px-4 py-2 text-sm text-red-600 hover:text-white bg-red-50 hover:bg-red-500 font-semibold rounded-xl transition-all duration-200 border-2 border-red-200 hover:border-red-500"
                >
                  Remove File
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className={`p-5 rounded-2xl transition-all duration-300 ${
                  isDragActive
                    ? 'bg-gradient-to-br from-[#1e73be] to-[#4A90C8] shadow-xl scale-110'
                    : 'bg-gradient-to-br from-gray-100 to-gray-200'
                }`}>
                  <Upload className={`w-12 h-12 transition-colors ${
                    isDragActive ? 'text-white' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <p className={`font-bold text-lg transition-colors ${
                    isDragActive ? 'text-[#1e73be]' : 'text-gray-700'
                  }`}>
                    {isDragActive ? 'Drop your PDF here' : 'Drag & drop a PDF file'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1.5 font-medium">
                    or click to browse (max 50 MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Extraction mode */}
          {/* <div>
            <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <div className="w-1 h-4 bg-gradient-to-b from-[#1e73be] to-[#4A90C8] rounded-full" />
              Extraction Mode
            </label>
            <div className="w-full p-5 rounded-2xl border-2 border-[#1e73be] bg-gradient-to-br from-blue-50 to-blue-100 text-[#1e73be] shadow-lg">
              <p className="text-lg font-bold">Anbima</p>
               <p className="text-sm text-gray-600 mt-1">Anbima</p> 
            </div>
          </div> */}

          {/* NQG-RQ-29: assign portfolios at upload time (client feedback) */}
          <div>
            <label className="block text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <div className="w-1 h-4 bg-gradient-to-b from-[#1e73be] to-[#4A90C8] rounded-full" />
              Portfolios <span className="text-xs font-medium text-gray-400">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {portfolios.filter((p) => !p.isDefault).map((p) => (
                <button
                  key={p._id}
                  type="button"
                  onClick={() => toggle(p.name)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    selectedPortfolios.includes(p.name)
                      ? 'bg-[#1e73be] text-white border-[#1e73be]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e73be]'
                  }`}
                >
                  {p.name}
                </button>
              ))}
              {/* newly-typed portfolios not yet in the list */}
              {selectedPortfolios
                .filter((n) => !portfolios.some((p) => p.name === n))
                .map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => toggle(n)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-[#1e73be] text-white border-[#1e73be]"
                  >
                    {n} (new)
                  </button>
                ))}
              <button
                type="button"
                onClick={addNew}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-dashed border-gray-300 text-gray-500 hover:border-[#1e73be] hover:text-[#1e73be]"
              >
                + New
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">
              Leave empty to use the default "FK" portfolio.
            </p>
          </div>

          {/* Enhanced Actions */}
          <div className="flex items-center gap-4 pt-4">
            <button
              onClick={onClose}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all duration-200 border-2 border-gray-300 hover:border-gray-400"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={onUpload}
              disabled={!uploadingFile || isUploading}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm bg-gradient-to-r from-[#ea7500] to-[#f59e42] hover:from-[#ea7500]/90 hover:to-[#f59e42]/90 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg border-2 border-orange-600/20 hover:-translate-y-0.5"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <BarChart3 className="w-5 h-5" />
                  Start Analysis
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfAnalysisList;
